/**
 * Runtime configuration, fetched rather than compiled in.
 *
 * Same mechanism and same reason as the widget's: this is a whitelabel image installed into infrastructure we
 * cannot see, and every value below differs per customer. `frontend/nginx/30-ddds-runtime-config.sh` writes
 * `config.json` when the container starts, from the same environment variables that produce the widget's CSP.
 *
 * The fallback is development only. If it ever served a deployment the symptom is an application that cannot
 * reach the API or the identity provider, which is loud - as opposed to a wrong-but-plausible default, which
 * would be quiet.
 */
import { z } from 'zod';

/** How often the exchange view re-reads, when the deployment says nothing. Seconds. */
export const DEFAULT_POLL_SECONDS = 15;
export const DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH = 2000;

/**
 * The scopes to request, when the deployment says nothing.
 *
 * Exactly the string that was hardcoded in `pkce.ts` until 2026-08-07, so an unconfigured deployment and
 * `vite dev` against the local Keycloak realm behave as they always did. That is the whole reason this has
 * a default rather than being required: local development still runs against Keycloak.
 *
 * It is not sufficient for Entra or Okta, and the difference is the point. Keycloak's realm has an
 * audience mapper, so `openid profile email` yields a token whose `aud` is already `ddds`. Entra has no
 * such thing: a scope that names no API yields a token audienced for Microsoft Graph, with a `nonce` in
 * its header, deliberately unverifiable by anybody else - so `verifyAccessToken` refuses it and the
 * symptom is a sign-in that completes at the provider and is then refused by us. A deployment against
 * either must name the API's own scope, and `offline_access` with it, because Entra returns no refresh
 * token without it while Keycloak returns one regardless.
 */
export const DEFAULT_IDENTITY_SCOPE = 'openid profile email';

/**
 * Let a real number or a non-blank string through to the coercion, and turn everything else into
 * `undefined` so the schema's `catch` supplies the default.
 *
 * Three peer cases as three statements rather than one expression, because the interesting one is the
 * middle: the entrypoint writes every scalar quoted, so an unset variable arrives as `""` - and
 * `Number("")` is `0`, which is a *valid* interval here meaning "never poll". Blanking it out is what
 * stops an unconfigured deployment reading as a deliberate instruction to switch polling off.
 */
function numericOrAbsent(value: unknown): unknown {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

const configSchema = z.object({
  /** Where `/api/v1` lives. Absolute, because this surface is on its own origin. */
  apiBaseUrl: z.string().min(1),
  /** The exact `iss` of the tenant, including the realm for Keycloak. Discovery hangs off it. */
  identityIssuer: z.string().min(1),
  /** A public client, so this is published in every redirect and is not a secret. */
  identityClientId: z.string().min(1),
  /**
   * The scopes the authorization request asks for. See `DEFAULT_IDENTITY_SCOPE` for why it is a value.
   *
   * `.min(1).catch(...)` rather than a bare optional, and the `min` is what makes the `catch` fire: the
   * entrypoint writes every scalar quoted, so an unset variable arrives as `""` - which is a perfectly
   * good string and would otherwise become a request for no scopes at all. Same trap `pollSeconds` closes
   * with `numericOrAbsent`, in the shape a string needs.
   *
   * `catch` rather than letting it fail the object, for the reason every other field here has one: this
   * schema is all-or-nothing, so a bad scope would take `apiBaseUrl` and the issuer down with it and drop
   * a deployed application onto its localhost fallback.
   */
  identityScope: z.string().min(1).catch(DEFAULT_IDENTITY_SCOPE),
  /**
   * How often the exchange view re-reads while the tab is visible, in seconds. 0 turns it off (DG.24).
   *
   * `catch` because this schema is all-or-nothing: a value that failed to parse would take `apiBaseUrl`
   * and the identity settings down with it and return the whole development fallback, so a deployed
   * application would look for the API on localhost because somebody mistyped an interval.
   *
   * The `preprocess` is not ceremony around the coercion, it is the whole correctness of it; see
   * `numericOrAbsent` for the empty-string trap it exists to close.
   *
   * The upper bound is not decoration either: `setInterval` above 2^31-1 milliseconds overflows and
   * fires immediately, so an absurd value would poll continuously rather than never.
   */
  pollSeconds: z.preprocess(numericOrAbsent, z.coerce.number().int().min(0).max(86_400)).catch(DEFAULT_POLL_SECONDS),
  /** Maximum length of a rep's authored formatted document request. */
  documentRequestMaxLength: z.preprocess(numericOrAbsent, z.coerce.number().int().min(100).max(50_000)).catch(DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH),
});

export type UiConfig = z.infer<typeof configSchema>;

export function devFallback(): UiConfig {
  return {
    apiBaseUrl: 'http://localhost:3000',
    identityIssuer: 'http://localhost:8081/realms/ddds',
    identityClientId: 'ddds-ui',
    identityScope: DEFAULT_IDENTITY_SCOPE,
    pollSeconds: DEFAULT_POLL_SECONDS,
    documentRequestMaxLength: DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH,
  };
}

export async function loadConfig(): Promise<UiConfig> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) return devFallback();
    const parsed = configSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : devFallback();
  } catch {
    // No file under `vite dev`, which serves `public/` and has none. A malformed one lands here too and is
    // treated the same way, because the alternative is a blank page explained only in a console.
    return devFallback();
  }
}

export const runtimeConfig: UiConfig = await loadConfig();
